import type { OptimizerRunV2State } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Box, Typography } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useRefreshedPortfolioReport } from '../../hooks/useRefreshedPortfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { isAuditSnapshotUnreliable } from '../../utils/audit/detectUnreliableAuditData';
import { resolvePortfolioReportPagePath } from '../../utils/presentation/getPortfolioReportPagePath';
import { shouldMergeLiveReportHoldings } from '../../utils/presentation/runProgress';
import { buildAuditPortfolioSnapshot } from '../../utils/snapshot/buildAuditPortfolioSnapshot';
import { buildReportDisplayMetadata } from '../../utils/snapshot/buildReportDisplayMetadata';
import { buildWeightDiffRows } from '../../utils/snapshot/buildWeightDiffRows';
import { BulkRebalanceOpenPortfolioButton } from './portfolioReviewActions';
import { BulkRebalanceReportDetailSummary } from './reportDetailSummary';
import { BulkRebalanceWeightDiffTable } from './weightDiffTable';

export type BulkRebalanceReportDetailSelection = {
  reportId: string;
  report?: PortfolioReport;
  audit?: OptimizerAuditV2Record;
  clientLabel: string;
  goalLabel: string;
};

export type BulkRebalanceReportDetailModalProps = {
  selection: BulkRebalanceReportDetailSelection | null;
  reportLoading?: boolean;
  tickerMap: Record<string, string>;
  runState?: OptimizerRunV2State;
  optimizerRunId?: string;
  onClose: () => void;
};

/** Fixed left columns (security + target + proposed + deviation) plus grid chrome. */
const SUMMARY_COLUMNS_WIDTH_PX = 560;
/** Approximate rendered width of a single sub-account column (input + arrow + gap). */
const SUBACCOUNT_COLUMN_WIDTH_PX = 220;
/** Upper bound so large portfolios still cap out near the previous `xl` width. */
const DIALOG_MAX_WIDTH_PX = 1400;
/** Failure / warning-only drawer — wide enough for validation copy without feeling cramped. */
const DIALOG_SUMMARY_MAX_WIDTH_PX = 640;
/** Leave room for header + summary + insights above a multi-line grid. */
const DIALOG_MAX_HEIGHT = '92vh';

export const BulkRebalanceReportDetailModal = ({
  selection,
  reportLoading = false,
  tickerMap,
  runState,
  optimizerRunId,
  onClose,
}: BulkRebalanceReportDetailModalProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance', 'accountTypes', 'components']);
  const mergeLiveReportHoldings = runState
    ? shouldMergeLiveReportHoldings(runState)
    : false;

  const refreshedReport = useRefreshedPortfolioReport(
    selection?.reportId,
    selection?.report,
    { enabled: mergeLiveReportHoldings },
  );

  const diffRows = useMemo(
    () => buildWeightDiffRows(selection?.audit, tickerMap),
    [selection?.audit, tickerMap],
  );

  const hasValidationFailure = selection?.audit?.passed === false;
  const hasTradeDetail = diffRows.length > 0;
  // An inconsistent input snapshot (market value == cash while still selling)
  // reconstructs to a phantom full liquidation, so hide the misleading grid and
  // let the summary surface a warning instead.
  const auditUnreliable = isAuditSnapshotUnreliable(selection?.audit);
  const showWeightDiffTable =
    hasTradeDetail && !hasValidationFailure && !auditUnreliable;

  const reportForTable = mergeLiveReportHoldings
    ? (refreshedReport ?? selection?.report)
    : selection?.report;

  // Size the dialog to the grid's visible sub-account columns so single-account
  // portfolios don't render with a wall of empty space. Uses the same snapshot
  // pipeline as the grid (default view = inactive sleeves hidden) to stay in sync.
  const visibleSubAccountCount = useMemo(() => {
    if (!showWeightDiffTable || !selection?.audit || !reportForTable) {
      return 0;
    }

    return buildAuditPortfolioSnapshot(
      selection.audit,
      tickerMap,
      buildReportDisplayMetadata(translateBackendString, reportForTable, t),
      { applyReportHoldings: mergeLiveReportHoldings },
    ).subAccounts.length;
  }, [
    mergeLiveReportHoldings,
    showWeightDiffTable,
    selection?.audit,
    reportForTable,
    tickerMap,
    t,
  ]);

  const dialogWidthPx = useMemo(
    () =>
      Math.min(
        SUMMARY_COLUMNS_WIDTH_PX +
          visibleSubAccountCount * SUBACCOUNT_COLUMN_WIDTH_PX,
        DIALOG_MAX_WIDTH_PX,
      ),
    [visibleSubAccountCount],
  );

  const title = selection
    ? [selection.clientLabel, selection.goalLabel].filter(Boolean).join(' · ')
    : '';
  const portfolioPagePath = useMemo(
    () =>
      resolvePortfolioReportPagePath(
        mergeLiveReportHoldings
          ? (refreshedReport ?? selection?.report)
          : selection?.report,
      ),
    [mergeLiveReportHoldings, refreshedReport, selection?.report],
  );

  return (
    <Dialog
      open={selection != null}
      onClose={onClose}
      maxWidth={showWeightDiffTable ? false : 'sm'}
      fullWidth
      sx={
        showWeightDiffTable
          ? {
              '.MuiDialog-paper': {
                maxWidth: `${dialogWidthPx}px`,
                maxHeight: DIALOG_MAX_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
              },
            }
          : {
              '.MuiDialog-paper': {
                maxWidth: `${DIALOG_SUMMARY_MAX_WIDTH_PX}px`,
                width: '100%',
              },
            }
      }
      data-testid='bulk-rebalance-report-detail-modal'
    >
      <DialogTitle
        onClose={onClose}
        testIds={{
          dialogCloseButton: 'bulk-rebalance-report-modal-close',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
            minWidth: 0,
            pr: 1,
          }}
        >
          <Typography
            variant='titleMedium'
            sx={{ flex: 1, minWidth: 0 }}
            noWrap
          >
            {title}
          </Typography>
          {portfolioPagePath && (
            <BulkRebalanceOpenPortfolioButton pagePath={portfolioPagePath} />
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          p: '0 !important',
        }}
      >
        {selection && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <BulkRebalanceReportDetailSummary
              reportId={selection.reportId}
              report={reportForTable}
              audit={selection.audit}
              diffRows={diffRows}
              clientLabel={selection.clientLabel}
              goalLabel={selection.goalLabel}
              reportLoading={reportLoading}
              optimizerRunId={optimizerRunId}
            />

            {showWeightDiffTable && reportForTable && (
              <BulkRebalanceWeightDiffTable
                key={selection.reportId}
                audit={selection.audit}
                tickerMap={tickerMap}
                report={reportForTable}
                applyReportHoldings={mergeLiveReportHoldings}
              />
            )}

            {diffRows.length === 0 && !selection.audit && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant='bodyMedium' color='textSecondary'>
                  {t('reports.noAuditDetail')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
